import React from 'react';
import { accentUtils, t } from '../../services/HelperService';
import { AccentPromptComposer, MAX_PROMPT_IMAGES, newPromptImageId } from './AccentPromptComposer';

const safeString = (v) => (v ?? '').toString();

const makeId = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;

/**
 * Prompt composer that appends user messages to a controlled messages array.
 *
 * Props:
 * - messages: the current messages array (controlled)
 * - onMessagesChange(nextMessages): called when a user sends a prompt
 * - onSendPrompt({ text, history, intent, images, messages }): async; history is
 *   role-aware oldest->newest; intent is 'ask' | 'change' when an explicit intent
 *   button was pressed (see AccentPromptComposer), undefined otherwise; images are
 *   the data URLs of screenshots pasted for THIS message (empty array when none);
 *   messages is the transcript as stored, including this message
 * - askLabel? / sendLabel?: enable and label the explicit intent buttons
 * - disabled?
 * - placeholder?
 *
 * Screenshots: pasting an image into the composer queues it as a removable
 * thumbnail; on send the data URLs ride both the stored message (m.images, for
 * transcript display and for the history of later turns) and the onSendPrompt call
 * (for the request). The server shows them to its models as images. A message may
 * be a screenshot alone — a placeholder text is substituted so the transcript and
 * the server history stay non-empty.
 *
 * Ref (imperative):
 * - send(text, intent): sends the given text exactly as if the user clicked Send —
 *   the message lands in the transcript (with intent label), onSendPrompt runs, and
 *   the Send button shows its built-in click spinner for the duration. Hosts that
 *   auto-start a conversation (e.g. profiles with autoFirstPrompt) should use this
 *   instead of calling onSendPrompt themselves.
 */
export const AccentPromptChat = React.forwardRef(({
  messages,
  onMessagesChange,
  onSendPrompt,
  disabled,
  promptLabel,
  placeholder,
  templateCatalog,
  readonly,
  askLabel,
  sendLabel,
  /** Character cap of the composer (its default suits chat prose; hosts that take pasted documentation raise it). */
  promptMax,
  /** Optional: observes the unsent draft text (hosts that hint on long pastes); never called on send. */
  onDraftChange,
  /** Session files (see AccentPromptComposer): passed through untouched. */
  attachments,
  onAttachFiles,
  onDropText,
  dropActive,
  onRemoveAttachment,
  onKeepAttachment,
  onConfirmAttachment,
  onOpenAttachment,
  imageHint,
}, ref) => {
  const list = React.useMemo(() => (Array.isArray(messages) ? messages : []), [messages]);
  const [value, setValue] = React.useState('');
  const [images, setImages] = React.useState([]);
  const onValueChange = React.useCallback((next) => {
    setValue(next);
    onDraftChange?.(safeString(next));
  }, [onDraftChange]);

  // The composer's paste handler resolves images asynchronously (decode+downscale),
  // so additions go through a functional update and re-check the cap here.
  const onAddImage = React.useCallback((img) => {
    setImages((prev) => (prev.length >= MAX_PROMPT_IMAGES ? prev : [...prev, img]));
  }, []);

  const onRemoveImage = React.useCallback((id) => {
    setImages((prev) => prev.filter((i) => i.id !== id));
  }, []);

  // doSend reads the images through a ref: the imperative send path (auto-first
  // prompts, intake answers) runs in whatever tick it likes.
  const imagesRef = React.useRef(images);
  imagesRef.current = images;

  const composerRef = React.useRef(null);

  React.useImperativeHandle(ref, () => ({
    send: (text, intent) => composerRef.current?.send(text, intent),
    // Attach an image from outside the composer (the preview region snip). Same
    // entry shape and cap as a paste; returns false when the cap is already reached
    // so the caller can tell the user instead of the image silently vanishing.
    addImage: (image) => {
      if (!image?.dataUrl || imagesRef.current.length >= MAX_PROMPT_IMAGES) return false;
      onAddImage({ id: safeString(image.id) || newPromptImageId(), dataUrl: image.dataUrl });
      return true;
    },
    imageCount: () => imagesRef.current.length,
    // Put text in the composer without sending it (a resumed session restores the
    // prompt of a turn that did not finish, so Send again is one click).
    setDraft: (text) => onValueChange(safeString(text)),
  }));

  const doSend = React.useCallback(async (text, intent) => {
    const sendImages = imagesRef.current.map((i) => i.dataUrl);
    // A screenshot may be the whole message; the transcript and the server history
    // both need non-empty text, so a placeholder stands in.
    const trimmed = safeString(text).trim() || (sendImages.length > 0 ? t('See the attached screenshot.') : '');
    if (accentUtils.isEmpty(trimmed)) return false;

    // Intent rides the stored message too, so later turns' server transcripts keep
    // which messages were explicit questions vs change requests. intentLabel is the
    // clicked button's text (client-side display only): the history renders it above
    // the bubble so the user can see which action each message was sent with.
    const intentLabel = intent === 'ask' ? safeString(askLabel) : (intent === 'change' ? safeString(sendLabel) : '');
    const msg = {
      id: makeId(),
      role: 'user',
      text: trimmed,
      ts: new Date().toISOString(),
      ...(intent ? { intent, intentLabel } : {}),
      ...(sendImages.length > 0 ? { images: sendImages } : {}),
    };
    // Never capped: trimming here once let system-message noise evict the real
    // conversation — including from the history handed to the server, so the AI lost
    // its context too. Transcript size is managed server-side at prompt-build time.
    const nextMessages = [...list, msg];
    onMessagesChange?.(nextMessages);

    // images stay on the history entries: the server attaches the most recent paste
    // to its model calls, so an earlier screenshot keeps grounding follow-up turns.
    const history = nextMessages.map(m => ({
      role: safeString(m?.role || 'user'),
      text: safeString(m?.text),
      intent: safeString(m?.intent),
      ...(Array.isArray(m?.images) && m.images.length > 0 ? { images: m.images } : {}),
    })).filter(m => !accentUtils.isEmpty(m.text));

    // messages: the full transcript entries INCLUDING this message, for hosts that
    // snapshot the transcript with the turn (onMessagesChange's state has not
    // re-rendered yet in this tick).
    const res = await onSendPrompt?.({ text: trimmed, history, intent, images: sendImages, messages: nextMessages });
    if (typeof res === 'boolean') return res;
    const ok = !!(res?.TestOk ?? res?.testOk ?? res?.ok ?? res?.success);
    if (typeof res === 'undefined') return true;
    return ok;
  }, [askLabel, list, onMessagesChange, onSendPrompt, sendLabel]);

  return (
    <AccentPromptComposer
      ref={composerRef}
      disabled={!!disabled || !!readonly}
      label={promptLabel}
      value={value}
      onChange={onValueChange}
      placeholder={placeholder}
      templateCatalog={templateCatalog}
      askLabel={askLabel}
      sendLabel={sendLabel}
      {...(promptMax > 0 ? { max: promptMax } : {})}
      images={images}
      onAddImage={onAddImage}
      onRemoveImage={onRemoveImage}
      imageHint={imageHint}
      attachments={attachments}
      onAttachFiles={onAttachFiles}
      onDropText={onDropText}
      dropActive={dropActive}
      onRemoveAttachment={onRemoveAttachment}
      onKeepAttachment={onKeepAttachment}
      onConfirmAttachment={onConfirmAttachment}
      onOpenAttachment={onOpenAttachment}
      onSend={async ({ text, intent }) => {
        const ok = await doSend(text, intent);
        if (ok) {
          setValue('');
          setImages([]);
          onDraftChange?.('');
        }
      }}
    />
  );
});
